/**
 * Is this file a video we can expect Mux to read? Checked when a coach picks
 * a file, before anything uploads (Coleman, 2026-10-08: Riley Rhodes's 3v2
 * clip went all the way through and Mux then rejected it as "not a valid
 * video or audio file", leaving a submission nobody could watch).
 *
 * Deliberately only stops what is clearly broken: an empty file, something
 * that isn't a video at all, or a format this browser normally plays that
 * won't load. A format the browser can't preview is not proof of anything
 * (Mux reads more than any browser does), so that goes through and Mux decides.
 */

const VIDEO_EXT = /\.(mp4|m4v|mov|qt|webm|mkv|avi|wmv|mpg|mpeg|3gp|3g2|hevc|mts|m2ts|ts|flv|ogv)$/i;

const BROKEN = "This file won't play as a video. If it's in iCloud or Google Photos, save it to your device first, or export it again, then pick it here.";

/** The quick checks that need no decoding. null when it passes. */
export function fileProblem(file) {
  if (!file) return "Pick a video file.";
  if (!file.size) return "This file is empty. Export it again and pick it here.";
  const isVideo = (file.type || "").startsWith("video/") || VIDEO_EXT.test(file.name || "");
  if (!isVideo) return "That isn't a video file. Pick an MP4, MOV or WebM.";
  return null;
}

/**
 * Resolves to null when the file looks fine, or a sentence for the coach.
 * Never rejects, and gives up after `timeoutMs` in the file's favor.
 */
export function checkVideoFile(file, { timeoutMs = 8000 } = {}) {
  const quick = fileProblem(file);
  if (quick) return Promise.resolve(quick);
  if (typeof document === "undefined" || typeof URL === "undefined" || !URL.createObjectURL) return Promise.resolve(null);
  return new Promise(resolve => {
    const v = document.createElement("video");
    const url = URL.createObjectURL(file);
    let done = false;
    const finish = (out) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      v.removeAttribute("src");
      try { v.load(); } catch { /* nothing to unload */ }
      URL.revokeObjectURL(url);
      resolve(out);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);
    v.preload = "metadata";
    v.muted = true;
    v.addEventListener("loadedmetadata", () => {
      finish(v.duration === 0 ? "This video has no length. Export it again and pick it here." : null);
    });
    v.addEventListener("error", () => {
      // Only a format this browser says it plays counts as evidence.
      let playable = "";
      try { playable = file.type ? v.canPlayType(file.type) : ""; } catch { playable = ""; }
      finish(playable ? BROKEN : null);
    });
    v.src = url;
  });
}
